import { RemoteConfigProperties } from "../../../../..";
import { NotImplementedError } from "../../../../../errors";
import { PromiseHandler } from "../../../../../handlers";
import { isLinearAdvertiserQueryResponse, parseDateFilter, parseReportTypeFilter, requireAnalysisInterval } from "../../../../../models/looker/modules/attribution";
import { toEmbedHash } from "../../../../../models/looker/query";
import { FILTER_OPTIONS_ADVERTISER_QUERY, FILTER_OPTIONS_LINEAR_ADVERTISER_QUERY } from "../query";
export default class LookerAttributionAdvertiserFilter extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-attribution-advertiser-filter");
    }
    async findItems(filters) {
        const { reportTypes } = parseReportTypeFilter(filters.where);
        // OVERLAP can't coexist with anything else (parseReportTypeFilter already
        // enforces that), so the only other mixed case is LINEAR alongside a
        // digital type. The Looker path intersects the two explores for that
        // combination (see makeAdvertiserQueries/intersectAdvertiserResults);
        // toAdvertiserApiCall below has no equivalent — it picks one endpoint —
        // so a mixed selection would silently drop the other channel's scoping.
        // Fall back to the Looker path rather than return the wrong advertisers.
        const isMixedLinearDigital = reportTypes.includes("LINEAR") && reportTypes.some((rt) => rt !== "LINEAR");
        if (this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.REPORTING_API_ATTRIBUTION_FILTERS) &&
            !isMixedLinearDigital) {
            return this.findItemsFromReportingApi(filters);
        }
        const queries = this.makeAdvertiserQueries(filters);
        const hashKey = await Promise.all(queries.map((queryBody) => this.sdk.looker.query.hashQuery(queryBody))).then((hashes) => hashes.join(""));
        return this.cache.promise(hashKey, () => Promise.all(queries.map((queryBody) => this.sdk.looker.query.run(queryBody).then(({ data, queryMeta }) => [
            {
                data: this.formatAdvertiserResponse(data),
                queryMeta
            }
        ])))
            .then((results) => {
            const commonAdvertisers = this.intersectAdvertiserLists(results.map((result) => result[0].data));
            return [
                {
                    data: commonAdvertisers,
                    // not ideal, but we don't use query metadata for this module. this is just to satisfy the type
                    queryMeta: results[0][0].queryMeta
                }
            ];
        })
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch conversion dashboard advertiser filter options");
        }));
    }
    /**
     * The same rows through the reporting API instead of the Looker SDK, in the
     * same shape, so no caller can tell which source answered. A failure is
     * surfaced rather than retried through Looker, so a broken endpoint cannot
     * present as working.
     */
    async findItemsFromReportingApi(filters) {
        const analysisInterval = requireAnalysisInterval(filters.where);
        const { reportTypes } = parseReportTypeFilter(filters.where);
        const calls = this.toAdvertiserApiCalls(reportTypes, analysisInterval);
        // Namespaced by transport — see Attribution.findItemsFromReportingApi.
        const hashKey = `api:${await this.sdk.looker.query.hashQuery(calls)}`;
        return this.cache.promise(hashKey, async () => {
            const context = "Failed to fetch conversion dashboard advertiser filter options.";
            // Settled, then unwrapped in call order, rather than Promise.all: when
            // both legs fail, Promise.all surfaces whichever lost the race, so a
            // transient linear failure could mask the cross-channel leg's
            // authoritative entitlement refusal. Cross-channel is first in the call
            // list for OVERLAP, so scanning in order makes its error win.
            const settled = await Promise.allSettled(calls.map((call) => this.runAdvertiserCall(call, context)));
            const rejected = settled.find((result) => result.status === "rejected");
            if (rejected) {
                throw rejected.reason;
            }
            const responses = settled.map((result) => result.value);
            return [
                {
                    // A single call intersects to itself, so this is the same answer a
                    // one-endpoint lookup always gave.
                    data: this.intersectAdvertiserLists(responses.map((response) => this.toAttributionFilterItems(response.rows))),
                    queryMeta: { embedHash: toEmbedHash(responses[0].shareUrl) }
                }
            ];
        });
    }
    async runAdvertiserCall(call, context) {
        const { data, query } = call.endpoint === "/attribution/linear/filter-options"
            ? await this.sdk.looker.reporting.query(call.endpoint, call.request, context)
            : await this.sdk.looker.reporting.query(call.endpoint, call.request, context);
        return { rows: data, shareUrl: query?.share_url };
    }
    linearAdvertiserCall(analysisInterval) {
        return {
            endpoint: "/attribution/linear/filter-options",
            request: {
                fields: ["advertiser_id", "advertiser_name"],
                filters: { analysis_interval: analysisInterval },
                sorts: ["advertiser_name asc"],
                limit: -1
            }
        };
    }
    crossChannelAdvertiserCall(analysisInterval, reportType) {
        return {
            endpoint: "/attribution/cross-channel/filter-options",
            request: {
                fields: ["advertiser_id", "advertiser_name"],
                filters: { analysis_interval: analysisInterval, report_type: reportType },
                sorts: ["advertiser_name asc"],
                limit: -1
            }
        };
    }
    /**
     * OVERLAP means "present in both explores", and each filter-options endpoint
     * answers for exactly one explore — so it runs both and intersects, the same
     * two queries and the same intersection the Looker path performs. Every other
     * report type lives in a single explore and is one call.
     */
    toAdvertiserApiCalls(reportTypes, analysisInterval) {
        if (reportTypes.includes("OVERLAP")) {
            return [
                this.crossChannelAdvertiserCall(analysisInterval, "OVERLAP"),
                this.linearAdvertiserCall(analysisInterval)
            ];
        }
        if (reportTypes.includes("LINEAR")) {
            return [this.linearAdvertiserCall(analysisInterval)];
        }
        return [this.crossChannelAdvertiserCall(analysisInterval, reportTypes.join(","))];
    }
    toAttributionFilterItems(rows) {
        const seenIds = new Set();
        const items = [];
        for (const row of rows) {
            if (row.advertiser_id && !seenIds.has(row.advertiser_id)) {
                items.push({ id: row.advertiser_id, name: row.advertiser_name ?? "" });
                seenIds.add(row.advertiser_id);
            }
        }
        return items;
    }
    /**
     * Only advertisers whose id appears in every list, in the first list's order
     * and taking names from it. Shared by both transports: an OVERLAP lookup is
     * two lists on either path.
     */
    intersectAdvertiserLists(lists) {
        if (lists.length === 0) {
            return [];
        }
        const otherIdSets = lists.slice(1).map((list) => new Set(list.map((item) => item.id)));
        return lists[0].filter((item) => otherIdSets.every((ids) => ids.has(item.id)));
    }
    makeAdvertiserQueries(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const parsedDateFilter = parseDateFilter(dateFilter);
        const { lookerMediaTypeFilter, reportTypes } = parseReportTypeFilter(filters.where);
        const queries = [];
        // Add digital advertisers query for VIDEO, AUDIO, DISPLAY, and OVERLAP report types
        if (reportTypes.some((rt) => ["VIDEO", "AUDIO", "DISPLAY", "OVERLAP"].includes(rt))) {
            const digitalAdvertisersQuery = {
                ...FILTER_OPTIONS_ADVERTISER_QUERY,
                filters: {
                    "conversion_filter_suggests.filter_date_nyc": parsedDateFilter,
                    ...lookerMediaTypeFilter
                }
            };
            queries.push(digitalAdvertisersQuery);
        }
        // Add linear commercials advertisers query for only LINEAR and OVERLAP report types
        if (reportTypes.some((rt) => ["LINEAR", "OVERLAP"].includes(rt))) {
            const linearAdvertisersQuery = {
                ...FILTER_OPTIONS_LINEAR_ADVERTISER_QUERY,
                filters: {
                    "conversion_linear_suggests.flt_date_nyc": parsedDateFilter
                }
            };
            queries.push(linearAdvertisersQuery);
        }
        // In intersectAdvertiserResults we find the intersection of all responses to ensure only advertisers present in all media types are shown
        return queries;
    }
    formatAdvertiserResponse(data) {
        if (isLinearAdvertiserQueryResponse(data)) {
            return data.map((advertiser) => ({
                name: advertiser["conversion_linear_suggests.advertiser_name"],
                id: advertiser["conversion_linear_suggests.advertiser_id"]
            }));
        }
        return data.map((advertiser) => ({
            name: advertiser["conversion_filter_suggests.advertiser_name"],
            id: advertiser["conversion_filter_suggests.advertiser_id"]
        }));
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=Advertiser.js.map